import { useTranslation } from 'react-i18next';
import type { PercentageCheckFilterState } from '../hooks/usePercentageCheckFilters';
import {
  PERCENTAGE_CHECK_RANGE_KEYS,
  type PercentageCheckFilterOptions,
  type PercentageCheckRangeKey,
} from '../types/PercentageCheck';
import { type FilterChip, FilterChipList } from './FilterChipList';

type FilterChipKey =
  | 'bucket'
  | 'includeZero'
  | 'professionalSearch'
  | 'unitIds'
  | 'contractTypeIds'
  | 'contractStatusIds'
  | PercentageCheckRangeKey;

type ChipsProps = {
  filterState: PercentageCheckFilterState;
  filterOptions: PercentageCheckFilterOptions | undefined;
};

const BUCKET_LABEL_KEYS = {
  below: 'abaixo',
  at100: 'cemPorcento',
  above: 'acima',
} as const;

const RANGE_COLUMN_KEYS = {
  maxPercentage: 'maximo',
  minPercentage: 'minimo',
  daysBelow: 'diasAbaixo',
  daysAbove: 'diasAcima',
} as const satisfies Record<PercentageCheckRangeKey, string>;

const PREFIX = 'alocacao:consultas.conferenciaPercentual';

export function PercentageCheckFilterChips({ filterState, filterOptions }: ChipsProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const { filters, state } = filterState;
  const chips: FilterChip<FilterChipKey>[] = [];

  const selected = (
    key: FilterChipKey,
    field: string,
    ids: number[] | undefined,
    names: Map<number, string>,
  ) => {
    if (!ids?.length) return;
    const name = ids.map((id) => names.get(id) ?? String(id)).join(', ');
    chips.push({ key, text: t('alocacao:filtros.chips.selecionado', { field, name }) });
  };

  if (filters.bucket && filters.bucket !== 'all') {
    chips.push({
      key: 'bucket',
      text: t('alocacao:filtros.chips.selecionado', {
        field: t(`${PREFIX}.colunas.classificacao`),
        name: t(`${PREFIX}.bucket.${BUCKET_LABEL_KEYS[filters.bucket]}`),
      }),
    });
  }
  if (!state.includeZero) {
    chips.push({ key: 'includeZero', text: t(`${PREFIX}.incluirZero`) });
  }
  if (filters.professionalSearch) {
    chips.push({
      key: 'professionalSearch',
      text: t('alocacao:filtros.chips.contem', {
        field: t(`${PREFIX}.colunas.profissional`),
        value: filters.professionalSearch,
      }),
    });
  }
  selected(
    'unitIds',
    t(`${PREFIX}.filtros.unidade`),
    filters.unitIds,
    new Map((filterOptions?.units ?? []).map((unit) => [unit.id, unit.code])),
  );
  selected(
    'contractTypeIds',
    t(`${PREFIX}.filtros.tipoContrato`),
    filters.contractTypeIds,
    new Map((filterOptions?.contractTypes ?? []).map((type) => [type.id, type.name])),
  );
  selected(
    'contractStatusIds',
    t(`${PREFIX}.filtros.situacaoContrato`),
    filters.contractStatusIds,
    new Map((filterOptions?.contractStatuses ?? []).map((status) => [status.id, status.name])),
  );
  for (const key of PERCENTAGE_CHECK_RANGE_KEYS) {
    const min = filters[`${key}From`];
    const max = filters[`${key}To`];
    const field = t(`${PREFIX}.colunas.${RANGE_COLUMN_KEYS[key]}`);
    if (min !== undefined && max !== undefined) {
      chips.push({ key, text: t(`${PREFIX}.filtros.faixaEntre`, { field, min, max }) });
    } else if (min !== undefined) {
      chips.push({ key, text: t(`${PREFIX}.filtros.faixaMinima`, { field, min }) });
    } else if (max !== undefined) {
      chips.push({ key, text: t(`${PREFIX}.filtros.faixaMaxima`, { field, max }) });
    }
  }

  const onRemove = (key: FilterChipKey) => {
    switch (key) {
      case 'bucket':
        filterState.setBucket('all');
        break;
      case 'includeZero':
        filterState.setIncludeZero(true);
        break;
      case 'professionalSearch':
        filterState.setSearch(null);
        break;
      case 'unitIds':
      case 'contractTypeIds':
      case 'contractStatusIds':
        filterState.setIds(key, []);
        break;
      default:
        filterState.setRange(key, { min: null, max: null });
    }
  };

  return <FilterChipList chips={chips} onRemove={onRemove} onClear={filterState.clearFilters} />;
}
